// ── UI PRIMITIVES ─────────────────────────────────────────────────────────────
// Shared base components used throughout the app.
// No business logic — pure presentation only.

import React, { useState, useRef, useEffect } from 'react';
import { statusColor, fmtMoney } from '../utils';

// ── DESIGN TOKENS ─────────────────────────────────────────────────────────────
const CS = {
  yellow:     '#F5C800',
  fuchsia:    '#D4007A',
  green:      '#1A5C38',
  black:      '#111111',
  white:      '#FFFFFF',
  border:     '#E0E0DC',
  muted:      '#999999',
  fontFamily: "'DM Sans', sans-serif",
};

// Base input style — shared across Inp, Txta, Sel, QtyTypeField
export var IS = {
  width: '100%',
  padding: '6px 9px',
  border: '0.5px solid #D8D7D2',
  borderRadius: 5,
  fontSize: 12,
  boxSizing: 'border-box',
  fontFamily: "'DM Sans', sans-serif",
  color: '#111111',
  outline: 'none',
  background: '#FFFFFF',
};

export function Badge(p) {
  return (
    <span style={{
      background: statusColor(p.status),
      color: '#fff',
      padding: '2px 10px',
      borderRadius: 12,
      fontSize: 11,
      fontWeight: 600,
    }}>
      {p.status}
    </span>
  );
}

export function TabBtn(p) {
  return (
    <button
      onClick={p.onClick}
      style={{
        padding: '8px 14px',
        border: 'none',
        borderBottom: p.active ? '2px solid #111111' : '2px solid transparent',
        background: 'none',
        fontFamily: "'DM Sans', sans-serif",
        fontWeight: 500,
        fontSize: 11,
        letterSpacing: '0.04em',
        color: p.active ? '#111111' : '#888880',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {p.label}
    </button>
  );
}

export function Lbl(p) {
  var star = p.required ? <span style={{ color: '#c0392b' }}> *</span> : null;
  return (
    <label style={{
      display: 'block',
      fontFamily: "'DM Sans', sans-serif",
      fontSize: 10,
      fontWeight: 500,
      letterSpacing: '0.07em',
      textTransform: 'uppercase',
      // color override (2026-07-29): fuchsia marks fields whose text
      // prints on EXTERNAL documents — the eye learns "magenta = the
      // client/trades will read this."
      color: p.color || CS.muted,
      marginBottom: 3,
    }}>
      {p.children}{star}
    </label>
  );
}

// Read-only text node used in lock mode. Renders the value as plain text
// in the same vertical slot a label+input would occupy. The data-field
// hook is preserved so test queries (and future agent diffing) can resolve
// either mode by the same selector. The text is inert — no edit affordance.
export function ReadValue(p) {
  return (
    <span data-field={p.dataField} style={{
      display: 'block',
      padding: '6px 0',
      fontSize: 12,
      fontFamily: "'DM Sans', sans-serif",
      color: '#111111',
      lineHeight: 1.4,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    }}>
      {p.children != null && p.children !== '' ? p.children : <span style={{ color: '#BBBBBB' }}>—</span>}
    </span>
  );
}

export function Inp(p) {
  var lbl = p.label ? <Lbl required={p.required}>{p.label}</Lbl> : null;
  if (p.locked) {
    return (
      <div style={{ marginBottom: 12 }}>
        {lbl}
        <ReadValue dataField={p.dataField}>{p.value}</ReadValue>
      </div>
    );
  }
  var symStyle = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: 12,
    color: '#999',
    fontWeight: 600,
    pointerEvents: 'none',
  };
  var hasPrefix = !!p.prefix;
  var hasSuffix = !!p.suffix;
  var inputStyle = Object.assign({}, IS,
    hasPrefix ? { paddingLeft: 22 } : {},
    hasSuffix ? { paddingRight: 28 } : {}
  );
  return (
    <div style={{ marginBottom: 12 }}>
      {lbl}
      <div style={{ position: 'relative' }}>
        {hasPrefix && <span style={{ ...symStyle, left: 8 }}>{p.prefix}</span>}
        <input
          data-field={p.dataField}
          type={p.type || 'text'}
          value={p.value || ''}
          onChange={function(e) { p.onChange(e.target.value); }}
          onFocus={p.onFocus}
          placeholder={p.placeholder || ''}
          style={inputStyle}
        />
        {hasSuffix && <span style={{ ...symStyle, right: 8 }}>{p.suffix}</span>}
      </div>
    </div>
  );
}

export function Txta(p) {
  var lbl = p.label ? <Lbl color={p.labelColor}>{p.label}</Lbl> : null;
  var hint = p.hint ? (
    <span style={{ fontSize: 10, color: p.labelColor || '#AAA8A3', fontStyle: 'italic',
                   marginLeft: 6, textTransform: 'none', letterSpacing: 0 }}>
      {p.hint}
    </span>
  ) : null;
  if (p.locked) {
    return (
      <div style={{ marginBottom: 12 }}>
        {lbl && hint ? (
          <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 3 }}>
            <Lbl color={p.labelColor} style={{ marginBottom: 0 }}>{p.label}</Lbl>{hint}
          </div>
        ) : lbl}
        <ReadValue dataField={p.dataField}>{p.value}</ReadValue>
      </div>
    );
  }
  return (
    <div style={{ marginBottom: 12 }}>
      {lbl && hint ? (
        <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 3 }}>
          <Lbl color={p.labelColor} style={{ marginBottom: 0 }}>{p.label}</Lbl>{hint}
        </div>
      ) : lbl}
      <textarea
        data-field={p.dataField}
        value={p.value || ''}
        onChange={function(e) { p.onChange(e.target.value); }}
        placeholder={p.placeholder || ''}
        rows={p.rows || 2}
        style={{ ...IS, resize: 'vertical' }}
      />
    </div>
  );
}

export function Sel(p) {
  var lbl = p.label ? <Lbl required={p.required}>{p.label}</Lbl> : null;
  if (p.locked) {
    return (
      <div style={{ marginBottom: 12 }}>
        {lbl}
        <ReadValue dataField={p.dataField}>{p.value}</ReadValue>
      </div>
    );
  }
  return (
    <div style={{ marginBottom: 12 }}>
      {lbl}
      <select
        data-field={p.dataField}
        value={p.value || ''}
        onChange={function(e) { p.onChange(e.target.value); }}
        style={{ ...IS, background: '#fff' }}
      >
        {p.children}
      </select>
    </div>
  );
}

// ── DROPDOWN — popover-button select (2026-07-28, tracker rebuild item 2) ────
// The sanctioned replacement for native <select> on new surfaces (Cimon:
// "nice buttons that click and open drop downs"). A real <button> trigger
// showing the current value (optional status dot), opening a hairline
// popover panel of options. Same contract as Sel: label / required /
// locked → ReadValue / dataField test hook.
//
// options: [{ value, label?, dot?, sub?, disabled? }]
//   dot: true → statusColor(value) zone dot; or a hex string; omit for none
//   sub: one muted line under the label (keep terse — one short rule)
// Test hooks: button carries data-field; panel [data-dropdown-panel]; each
// option [data-option-value="<value>"].
//
// Design-language notes: white ground + hairline borders only (open state
// cue = 1.5px ink border, the standard active-state weight); option hover
// uses GROUND_OFF (#FAFAF8), the one sanctioned second surface; selected
// option = medium weight + fuchsia ✓, never a tinted fill. Keyboard:
// Enter/Space/ArrowDown open, arrows move, Enter selects, Esc closes.
export function Dropdown(p) {
  var lbl = p.label ? <Lbl required={p.required}>{p.label}</Lbl> : null;
  var options = p.options || [];
  var os = useState(false); var open = os[0]; var setOpen = os[1];
  var his = useState(-1); var hi = his[0]; var setHi = his[1];
  var rootRef = useRef(null);
  var panelRef = useRef(null);

  useEffect(function() {
    if (!open) return;
    function onDocDown(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocDown);
    return function() { document.removeEventListener('mousedown', onDocDown); };
  }, [open]);

  // Keep the highlighted option scrolled into view while arrowing.
  useEffect(function() {
    if (!open || hi < 0 || !panelRef.current) return;
    var node = panelRef.current.querySelector('[data-option-index="' + hi + '"]');
    if (node && node.scrollIntoView) node.scrollIntoView({ block: 'nearest' });
  }, [open, hi]);

  if (p.locked) {
    var lockedOpt = options.find(function(o) { return o.value === p.value; });
    return (
      <div style={{ marginBottom: 12 }}>
        {lbl}
        <ReadValue dataField={p.dataField}>{(lockedOpt && (lockedOpt.label || lockedOpt.value)) || p.value}</ReadValue>
      </div>
    );
  }

  var selected = options.find(function(o) { return o.value === p.value; }) || null;

  function dotColorFor(o) {
    if (!o || !o.dot) return null;
    return o.dot === true ? statusColor(o.value) : o.dot;
  }

  function openPanel() {
    var idx = options.findIndex(function(o) { return o.value === p.value; });
    setHi(idx >= 0 ? idx : options.findIndex(function(o) { return !o.disabled; }));
    setOpen(true);
  }

  function pick(o) {
    if (!o || o.disabled) return;
    setOpen(false);
    if (p.onChange) p.onChange(o.value);
  }

  function move(delta) {
    if (!options.length) return;
    var i = hi;
    for (var step = 0; step < options.length; step++) {
      i = (i + delta + options.length) % options.length;
      if (!options[i].disabled) { setHi(i); return; }
    }
  }

  function onKeyDown(e) {
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault(); openPanel();
      }
      return;
    }
    if (e.key === 'Escape') { e.preventDefault(); setOpen(false); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); move(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(options[hi]); }
    else if (e.key === 'Tab') { setOpen(false); }
  }

  var selDot = dotColorFor(selected);
  return (
    <div style={{ marginBottom: 12 }}>
      {lbl}
      <div ref={rootRef} style={{ position: 'relative' }}>
        <button
          type="button"
          data-field={p.dataField}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={function() { open ? setOpen(false) : openPanel(); }}
          onKeyDown={onKeyDown}
          style={Object.assign({}, IS, {
            display: 'flex', alignItems: 'center', gap: 8,
            width: p.width || '100%', textAlign: 'left', cursor: 'pointer',
            background: '#fff',
            border: open ? '1.5px solid #111111' : IS.border,
            // Compensate the border-weight change so the row never shifts.
            padding: open ? '5px 8px' : IS.padding,
          })}
        >
          {selDot && <span style={{ width: 8, height: 8, borderRadius: '50%', background: selDot, flex: 'none' }} />}
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                         color: selected ? '#111111' : '#BBBBBB' }}>
            {selected ? (selected.label || selected.value) : (p.placeholder || 'Select…')}
          </span>
          <span aria-hidden="true" style={{ fontSize: 9, color: '#888880', flex: 'none',
                                            transform: open ? 'rotate(180deg)' : 'none' }}>▾</span>
        </button>
        {open && (
          <div
            ref={panelRef}
            data-dropdown-panel="true"
            role="listbox"
            style={{
              position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 3,
              background: '#fff', border: '0.5px solid #D8D7D2', borderRadius: 6,
              boxShadow: '0 4px 18px rgba(0,0,0,0.10)', zIndex: 300,
              maxHeight: 280, overflowY: 'auto', padding: '4px 0',
            }}
          >
            {options.map(function(o, i) {
              var isSel = o.value === p.value;
              var dot = dotColorFor(o);
              return (
                <div
                  key={o.value}
                  role="option"
                  aria-selected={isSel}
                  data-option-value={o.value}
                  data-option-index={i}
                  onMouseEnter={function() { if (!o.disabled) setHi(i); }}
                  onMouseDown={function(e) { e.preventDefault(); }}
                  onClick={function() { pick(o); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '7px 12px',
                    cursor: o.disabled ? 'not-allowed' : 'pointer',
                    background: !o.disabled && hi === i ? '#FAFAF8' : '#fff',
                    opacity: o.disabled ? 0.45 : 1,
                  }}
                >
                  {dot && <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot, flex: 'none' }} />}
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 12, color: '#111111',
                                   fontWeight: isSel ? 600 : 400,
                                   fontFamily: "'DM Sans', sans-serif" }}>
                      {o.label || o.value}
                    </span>
                    {o.sub && (
                      <span style={{ display: 'block', fontSize: 10, color: '#888880', marginTop: 1,
                                     fontFamily: "'DM Sans', sans-serif" }}>
                        {o.sub}
                      </span>
                    )}
                  </span>
                  {isSel && <span style={{ fontSize: 11, color: CS.fuchsia, flex: 'none', fontWeight: 600 }}>✓</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export function Row(p) {
  return (
    <div style={{ display: 'flex', gap: p.gap || 12, flexWrap: 'wrap' }}>
      {p.children}
    </div>
  );
}

export function SHead(p) {
  return (
    <div style={{
      fontFamily: "'DM Sans', sans-serif",
      fontSize: 9,
      fontWeight: 500,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: '#888880',
      marginBottom: 8,
      paddingBottom: 5,
      paddingTop: 10,
      borderBottom: '0.5px solid #EFEFEC',
    }}>
      {p.children}
    </div>
  );
}

export function Btn(p) {
  var vs = {
    primary:   { background: '#111111', color: '#ffffff', border: '1.5px solid #111111' },
    secondary: { background: '#ffffff', color: '#111111', border: '1.5px solid #111111' },
    ghost:     { background: '#ffffff', color: '#999999', border: '1.5px solid #E0E0DC' },
    success:   { background: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7' },
    danger:    { background: '#ffebee', color: '#c62828', border: '1px solid #ef9a9a' },
    warning:   { background: '#fff8e1', color: '#e65100', border: '1px solid #ffe082' },
    purple:    { background: '#ffffff', color: '#111111', border: '1.5px solid #111111' },
    blue:      { background: '#e3f2fd', color: '#0277bd', border: '1px solid #90caf9' },
  };
  var v = vs[p.variant || 'primary'];
  return (
    <button
      onClick={p.onClick}
      disabled={p.disabled}
      data-testid={p['data-testid']}
      style={{
        ...v,
        padding: p.small ? '4px 10px' : '7px 18px',
        borderRadius: 6,
        fontFamily: "'DM Sans', sans-serif",
        fontWeight: 500,
        fontSize: 12,
        cursor: p.disabled ? 'not-allowed' : 'pointer',
        opacity: p.disabled ? 0.5 : 1,
        whiteSpace: 'nowrap',
      }}
    >
      {p.children}
    </button>
  );
}

export function SavedBadge(p) {
  return p.on
    ? <span style={{ fontSize: 11, color: '#2e7d32', fontWeight: 600 }}>✓ Saved</span>
    : null;
}

export function BackupStatus() {
  var backupTime = localStorage.getItem('csid_backup_time');
  if (!backupTime) return null;
  var d = new Date(backupTime);
  var now = new Date();
  var mins = Math.floor((now - d) / 60000);
  var label = mins < 1 ? 'just now' : mins < 60 ? mins + 'm ago' : Math.floor(mins / 60) + 'h ago';
  return <span style={{ fontSize: 10, color: '#aaa', marginLeft: 8 }}>Backed up {label}</span>;
}

export function StatusDot(p) {
  return (
    <div style={{
      width: 8,
      height: 8,
      borderRadius: '50%',
      background: statusColor(p.status),
      flexShrink: 0,
    }} />
  );
}

export function QtyTypeField(p) {
  var QTY_TYPES = ['Each','Pair','Set','Linear Foot','Square Foot','Yard','Roll','Panel','Sheet','Lot'];
  var cs = useState(false); var custom = cs[0]; var setCustom = cs[1];
  var isPreset = QTY_TYPES.indexOf(p.value) >= 0 || p.value === '';
  if (p.locked) {
    return (
      <div style={{ marginBottom: 12 }}>
        <Lbl>Quantity Type</Lbl>
        <ReadValue dataField={p.dataField || 'qtyType'}>{p.value}</ReadValue>
      </div>
    );
  }
  if (custom || !isPreset) {
    return (
      <div style={{ marginBottom: 12 }}>
        <Lbl>Quantity Type</Lbl>
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            data-field={p.dataField || 'qtyType'}
            value={p.value || ''}
            onChange={function(e) { p.onChange(e.target.value); }}
            placeholder="e.g. Square Yard"
            style={IS}
          />
          <button
            onClick={function() { setCustom(false); p.onChange(''); }}
            style={{ padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 11, cursor: 'pointer', background: '#fff', color: '#888' }}
          >
            ↩
          </button>
        </div>
      </div>
    );
  }
  return (
    <div style={{ marginBottom: 12 }}>
      <Lbl>Quantity Type</Lbl>
      <select
        data-field={p.dataField || 'qtyType'}
        value={p.value || ''}
        onChange={function(e) {
          if (e.target.value === '__custom__') { setCustom(true); p.onChange(''); }
          else { p.onChange(e.target.value); }
        }}
        style={{ ...IS, background: '#fff' }}
      >
        <option value="">— Select type —</option>
        {QTY_TYPES.map(function(t) { return <option key={t} value={t}>{t}</option>; })}
        <option value="__custom__">+ Custom...</option>
      </select>
    </div>
  );
}

// Currency input — text field with format-on-blur. Displays $1,812.80 when
// blurred; reveals the raw decimal (e.g. 1812.80) on focus for editing.
// Invalid input keeps the last valid value; empty blurs to 0.
//
// Props:
//   value          number | null | ''   — current numeric value
//   onValueChange  (n:number) => void   — called on blur with parsed value
//   disabled       boolean              — read-only when true
//   style          object               — overrides IS
//   ...rest        spread onto <input>  — data-testid, title, placeholder
export function CurrencyInput(p) {
  var fs = useState(false);  var focused = fs[0]; var setFocused = fs[1];
  var rs = useState('');     var rawText = rs[0]; var setRawText = rs[1];

  function handleFocus(e) {
    var n = Number(p.value);
    var seed = (p.value == null || p.value === '' || isNaN(n) || n === 0) ? '' : String(n);
    setRawText(seed);
    setFocused(true);
    if (p.onFocus) p.onFocus(e);
  }
  function handleChange(e) {
    setRawText(e.target.value);
  }
  function handleBlur(e) {
    setFocused(false);
    var cleaned = String(rawText || '').replace(/[$,\s]/g, '');
    if (cleaned === '') {
      if (p.onValueChange) p.onValueChange(0);
    } else {
      var n = parseFloat(cleaned);
      if (!isNaN(n)) {
        var rounded = Math.round(n * 100) / 100;
        if (p.onValueChange) p.onValueChange(rounded);
      }
      // isNaN(n) → reject silently, keep last valid value
    }
    if (p.onBlur) p.onBlur(e);
  }

  // ''/null render BLANK when blurred (2026-08-10 — an empty fee field
  // means "no value recorded", which must not read as an explicit $0.00).
  var displayValue = focused ? rawText
    : (p.value == null || p.value === '') ? ''
    : fmtMoney(p.value);

  var rest = Object.assign({}, p);
  delete rest.value; delete rest.onValueChange; delete rest.style;
  delete rest.onFocus; delete rest.onChange; delete rest.onBlur;
  delete rest.disabled;

  return (
    <input
      type="text"
      inputMode="decimal"
      value={displayValue}
      disabled={p.disabled}
      onFocus={handleFocus}
      onChange={handleChange}
      onBlur={handleBlur}
      style={p.style || IS}
      {...rest}
    />
  );
}
